/**
 * Helper functions for working with FormData objects
 */

/**
 * Convert a plain JavaScript object to FormData
 */
export const objectToFormData = (obj: Record<string, any>): FormData => {
  const formData = new FormData();

  const appendToFormData = (key: string, value: any) => {
    if (value === null || value === undefined) {
      return;
    }

    if (value instanceof File) {
      formData.append(key, value);
    } else if (value instanceof FileList) {
      Array.from(value).forEach((file, index) => {
        formData.append(`${key}[${index}]`, file);
      });
    } else if (Array.isArray(value)) {
      value.forEach((item, index) => {
        if (item instanceof File) {
          formData.append(`${key}[${index}]`, item);
        } else if (typeof item === "object" && item !== null) {
          Object.entries(item).forEach(([nestedKey, nestedValue]) => {
            appendToFormData(`${key}[${index}].${nestedKey}`, nestedValue);
          });
        } else {
          formData.append(`${key}[${index}]`, String(item));
        }
      });
    } else if (typeof value === "object" && value !== null) {
      Object.entries(value).forEach(([nestedKey, nestedValue]) => {
        appendToFormData(`${key}.${nestedKey}`, nestedValue);
      });
    } else {
      formData.append(key, String(value));
    }
  };

  Object.entries(obj).forEach(([key, value]) => {
    appendToFormData(key, value);
  });

  return formData;
};

/**
 * Convert FormData to a plain JavaScript object
 */
export const formDataToObject = (formData: FormData): Record<string, any> => {
  const obj: Record<string, any> = {};

  for (const [key, value] of formData.entries()) {
    if (obj[key]) {
      if (Array.isArray(obj[key])) {
        obj[key].push(value);
      } else {
        obj[key] = [obj[key], value];
      }
    } else {
      obj[key] = value;
    }
  }

  return obj;
};

/**
 * Create FormData for file upload with additional metadata
 */
export const createFileUploadFormData = (
  files: File | File[],
  metadata?: Record<string, any>
): FormData => {
  const formData = new FormData();

  if (Array.isArray(files)) {
    files.forEach((file, index) => {
      formData.append(`files[${index}]`, file);
    });
  } else {
    formData.append("file", files);
  }

  if (metadata) {
    Object.entries(metadata).forEach(([key, value]) => {
      formData.append(key, String(value));
    });
  }

  return formData;
};

/**
 * Create FormData for image upload with specific options
 */
export const createImageUploadFormData = (
  image: File,
  options?: {
    alt?: string;
    caption?: string;
    category?: string;
    tags?: string[];
    metadata?: Record<string, any>;
  }
): FormData => {
  const formData = new FormData();

  formData.append("image", image);

  if (options?.alt) {
    formData.append("alt", options.alt);
  }

  if (options?.caption) {
    formData.append("caption", options.caption);
  }

  if (options?.category) {
    formData.append("category", options.category);
  }

  if (options?.tags && options.tags.length > 0) {
    options.tags.forEach((tag, index) => {
      formData.append(`tags[${index}]`, tag);
    });
  }

  if (options?.metadata) {
    Object.entries(options.metadata).forEach(([key, value]) => {
      formData.append(`metadata.${key}`, String(value));
    });
  }

  return formData;
};

/**
 * Create FormData for bulk file upload
 */
export const createBulkUploadFormData = (
  files: File[],
  options?: {
    folder?: string;
    overwrite?: boolean;
    metadata?: Record<string, any>;
  }
): FormData => {
  const formData = new FormData();

  files.forEach((file, index) => {
    formData.append(`files[${index}]`, file);
  });

  if (options?.folder) {
    formData.append("folder", options.folder);
  }

  if (options?.overwrite !== undefined) {
    formData.append("overwrite", String(options.overwrite));
  }

  if (options?.metadata) {
    formData.append("metadata", JSON.stringify(options.metadata));
  }

  return formData;
};

/**
 * Validate file type
 */
export const validateFileType = (
  file: File,
  allowedTypes: string[]
): boolean => {
  return allowedTypes.includes(file.type);
};

/**
 * Validate file size
 */
export const validateFileSize = (file: File, maxSizeInMB: number): boolean => {
  const maxSizeInBytes = maxSizeInMB * 1024 * 1024;
  return file.size <= maxSizeInBytes;
};

/**
 * Get file extension
 */
export const getFileExtension = (filename: string): string => {
  return filename.split(".").pop()?.toLowerCase() || "";
};

/**
 * Check if file is an image
 */
export const isImageFile = (file: File): boolean => {
  return file.type.startsWith("image/");
};

/**
 * Check if file is a document
 */
export const isDocumentFile = (file: File): boolean => {
  const documentTypes = [
    "application/pdf",
    "application/msword",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    "application/vnd.ms-excel",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    "text/plain",
  ];

  return documentTypes.includes(file.type);
};
